import { useEffect, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { ArrowLeft, Loader2, Trash2, Users } from "lucide-react";
import {
  useCreateGroup,
  useDeleteGroup,
  useHostGroup,
  useUpdateGroup,
} from "@/lib/api/useGroups";
import { useToast } from "@/components/ux/Toast";
import { GroupImageEditor } from "../components/GroupImageEditor";
import { TagSelector } from "../components/TagSelector";
import { useConfirm } from "@/components/ConfirmModal";

export default function GroupEdit() {
  const [, params] = useRoute("/g/:slug/edit");
  const isEditing = !!params?.slug;
  const slug = params?.slug ?? null;

  const { data: existing, isLoading: loadingExisting } = useHostGroup(
    isEditing ? slug : null,
  );

  const [, navigate] = useLocation();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [imageEditorOpen, setImageEditorOpen] = useState(false);

  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup(slug ?? "");
  const deleteGroup = useDeleteGroup(slug ?? "");

  const isSaving = createGroup.isPending || updateGroup.isPending;

  // Pre-fill form when editing
  useEffect(() => {
    if (existing?.success && existing.data) {
      const g = existing.data;
      setName(g.name);
      setDescription(g.description ?? "");
      setImageUrl(g.imageUrl ?? "");
      setTags((g.tags ?? []) as string[]);
    }
  }, [existing]);

  useEffect(() => {
    document.title = isEditing ? "Edit Group — Outclose" : "New Group — Outclose";
  }, [isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      imageUrl: imageUrl || null,
      tags,
    };

    try {
      if (isEditing) {
        await updateGroup.mutateAsync(body);
        showToast("Group updated", "success");
        navigate(`/g/${slug}`);
      } else {
        const result = await createGroup.mutateAsync(body);
        if (result.success && result.data) {
          showToast("Group created", "success");
          navigate(`/g/${result.data.slug}`);
        }
      }
    } catch {
      showToast("Something went wrong. Try again.", "error");
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Delete group?",
      message:
        "This will unlink all events from this group. The events themselves won't be deleted.",
      confirmText: "Delete",
      type: "danger",
    });
    if (!ok) return;
    try {
      await deleteGroup.mutateAsync();
      showToast("Group deleted", "success");
      navigate("/app/profile");
    } catch {
      showToast("Failed to delete group", "error");
    }
  };

  if (isEditing && loadingExisting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28 md:pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-4">
        <Link
          href={isEditing ? `/g/${slug}` : "/app/profile"}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 hover:text-primary-500 transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {isEditing ? "Back to group" : "Back to profile"}
        </Link>

        <h1 className="text-2xl font-bold mb-6">
          {isEditing ? "Edit group" : "New group"}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Group photo */}
          <div>
            <label className="block text-sm font-medium mb-3">Group photo</label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setImageEditorOpen(true)}
                className="relative shrink-0 group"
                aria-label="Edit group photo"
              >
                {imageUrl ? (
                  <img src={imageUrl} alt="Group" className="w-20 h-20 rounded-full object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500/60 via-secondary-500/50 to-accent-500/60 flex items-center justify-center">
                    <Users className="w-8 h-8 text-white/80" />
                  </div>
                )}
                <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-xs font-medium">Edit</span>
                </div>
              </button>
              <div className="space-y-1.5">
                <button type="button" onClick={() => setImageEditorOpen(true)} className="btn btn-secondary btn-sm">
                  {imageUrl ? "Change photo" : "Add photo"}
                </button>
                {imageUrl && (
                  <button type="button" onClick={() => setImageUrl("")} className="block text-xs text-rose-500 hover:underline">
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="group-name" className="block text-sm font-medium mb-1.5">
              Name <span className="text-rose-500">*</span>
            </label>
            <input
              id="group-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Queer Dinner Club NYC"
              maxLength={100}
              required
              className="input input-bordered w-full"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="group-desc" className="block text-sm font-medium mb-1.5">
              Description
            </label>
            <textarea
              id="group-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell people what this group is about…"
              rows={4}
              maxLength={2000}
              className="textarea textarea-bordered w-full resize-none"
            />
          </div>

          {/* Tags */}
          <TagSelector
            label="Tags"
            value={tags}
            onChange={setTags}
            max={10}
            placeholder="Pick event types or add your own…"
          />

          {/* Submit */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="btn btn-primary flex-1 sm:flex-none sm:min-w-32"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                "Save changes"
              ) : (
                "Create group"
              )}
            </button>

            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteGroup.isPending}
                className="btn btn-ghost text-rose-500 hover:bg-rose-500/10 ml-auto"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Delete
              </button>
            )}
          </div>
        </form>
      </div>

      <GroupImageEditor
        isOpen={imageEditorOpen}
        onClose={() => setImageEditorOpen(false)}
        onChange={(url) => { setImageUrl(url); setImageEditorOpen(false); }}
      />
    </div>
  );
}
